import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import MicRoundedIcon from '@mui/icons-material/MicRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import { Alert, Box, Button, Chip, Fade, IconButton, Stack, TextField } from '@mui/material';
import { buildAttachmentMarker } from '@prospero/shared/src/assistant/assistant-attachment-marker';
import { motion } from 'motion/react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePrefersReducedMotion } from '../../hooks/use-prefers-reduced-motion';
import { RecordingWaveform } from './voice/recording-waveform';

/**
 * DIRECTION CONTRACT — Prospero assistant standard (shaped directly, no roll:
 * precisely-specified redesign inside the established product world).
 *
 * THESIS: the composer is an invariant ground line — text and voice swap
 * *inside the same slot* at the same 56px height, so no recording state ever
 * moves a pixel of the panel. This is what the previous design refused (a
 * caption row grew the composer and the whole panel jumped).
 *
 * OWN-WORLD: light Prospero register; pill field (28px radius, paper ground,
 * hairline border); three fixed 48px control slots on the right whose
 * contents crossfade; brand purple only on the primary send and the voice
 * wave; red only for "live" and stop.
 *
 * STORY: the operator types, attaches, or holds the mic; while recording they
 * see time and voice inside the field itself and can discard (X) or send
 * (stop) without the layout telling them anything changed.
 *
 * FIRST VIEWPORT: one row — [field slot][attach|cancel][mic|live dot]
 * [send|stop] — with the attachment strip as the only conditional row above.
 *
 * FINISH: unreviewed and undocumented is unfinished; this build ends with the
 * finish review, the verdict, and DESIGN.md.
 */

interface PendingAttachment {
  attachmentId: string;
  filename: string;
  /**
   * Human caption naming the interview field this photo was attached for —
   * set only when the pending attachment came from the deterministic
   * "Aggiungi foto" NC button (Fase 3 side-fix), never typed by the
   * operator. Embedded in the outgoing `[allegato: … nota:…]` marker so the
   * caption survives even when the operator answers by typing instead of
   * tapping a chip.
   */
  fieldCaption?: string;
}

export interface AssistantComposerProps {
  isRecording: boolean;
  isSending: boolean;
  pageUrl: string;
  seconds: number;
  /**
   * Hides the microphone control (voice is disabled by prop, not CSS, so the
   * recorder never mounts a live control on pages whose turns cannot carry the
   * page context — e.g. the survey studio, where a voice turn would run as a
   * normal turn on the active tenant).
   */
  voiceDisabled?: boolean;
  sendMessage: (text: string, pageUrl: string) => Promise<unknown>;
  sendVoiceMessage: (blob: Blob, pageUrl: string) => Promise<unknown>;
  start: () => Promise<void>;
  stop: () => Promise<Blob>;
  cancel: () => void;
  uploadAttachment: (file: File) => Promise<{ attachmentId: string; filename: string }>;
  onAttachmentChange: (attachment: PendingAttachment | null) => void;
  attachment: PendingAttachment | null;
  attachmentError: string | null;
  onAttachmentError: (error: string | null) => void;
}

function swallow(operation: Promise<unknown>) {
  void operation.catch(() => undefined);
}

// Created once at module scope: motion owns the `transform`/`opacity` on this
// node, so no conflicting static value may come back into its `sx`.
const MotionDot = motion.create(Box);

/** The middle control slot while recording: a passive pulsing "live" dot. */
export function RecordingLiveDot() {
  const reduceMotion = usePrefersReducedMotion();
  return (
    <Box
      aria-hidden="true"
      sx={{
        alignItems: 'center',
        display: 'flex',
        height: 48,
        justifyContent: 'center',
        width: 48,
      }}
    >
      <MotionDot
        animate={reduceMotion ? undefined : { opacity: [1, 0.3, 1], scale: [1, 0.75, 1] }}
        sx={{ bgcolor: 'error.main', borderRadius: '50%', height: 10, width: 10 }}
        transition={{ duration: 1.4, ease: 'easeInOut', repeat: Infinity }}
      />
    </Box>
  );
}

/**
 * The platform-assistant composer. The field slot keeps one geometry across
 * idle and recording states; the three control slots swap contents in place
 * (attach↔discard, mic↔live dot, send↔stop), so the row — and everything
 * above it — never shifts.
 */
export function AssistantComposer({
  isRecording,
  isSending,
  pageUrl,
  seconds,
  voiceDisabled = false,
  sendMessage,
  sendVoiceMessage,
  start,
  stop,
  cancel,
  uploadAttachment,
  attachment,
  onAttachmentChange,
  attachmentError,
  onAttachmentError,
}: AssistantComposerProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const submitDraft = () => {
    const text = draft.trim();
    if (isRecording || isSending || (!text && !attachment)) {
      return;
    }
    let finalText = text;
    if (attachment) {
      finalText = `${text}\n${buildAttachmentMarker(attachment)}`;
      onAttachmentChange(null);
    }
    setDraft('');
    swallow(sendMessage(finalText, pageUrl));
  };
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    onAttachmentError(null);
    try {
      const result = await uploadAttachment(file);
      onAttachmentChange(result);
    } catch {
      onAttachmentError(file.name);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleComposerKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.nativeEvent.isComposing || event.keyCode === 229) {
      return;
    }
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      submitDraft();
    }
  };
  const stopRecording = async () => {
    const blob = await stop();
    if (blob.size > 0) {
      swallow(sendVoiceMessage(blob, pageUrl));
    }
  };
  return (
    <Box>
      {attachment || attachmentError ? (
        <Box sx={{ px: 2, pt: 1 }}>
          {attachment ? (
            <Chip
              icon={<CheckRoundedIcon />}
              label={t('guida.assistant.attachmentReady')}
              onDelete={() => onAttachmentChange(null)}
              size="small"
              sx={{ maxWidth: '100%' }}
              variant="outlined"
            />
          ) : null}
          {attachmentError ? (
            <Alert
              action={
                <Button color="inherit" onClick={() => fileInputRef.current?.click()} size="small">
                  {t('guida.assistant.retry')}
                </Button>
              }
              severity="error"
            >
              {t('guida.assistant.attachmentFailed', { filename: attachmentError })}
            </Alert>
          ) : null}
        </Box>
      ) : null}
      <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end', p: 2 }}>
        <input
          accept="application/pdf,image/jpeg,image/png"
          data-testid="attachment-input"
          onChange={handleFileSelect}
          ref={fileInputRef}
          style={{ display: 'none' }}
          type="file"
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {isRecording ? (
            <RecordingWaveform seconds={seconds} />
          ) : (
            <Fade appear in timeout={150}>
              <TextField
                fullWidth
                maxRows={5}
                minRows={1}
                multiline
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={handleComposerKeyDown}
                placeholder={t('guida.assistant.composerPlaceholder', {
                  defaultValue: 'Scrivi qui la tua domanda…',
                })}
                slotProps={{
                  htmlInput: {
                    'aria-label': t('guida.assistant.composerLabel', {
                      defaultValue: 'Scrivi un messaggio',
                    }),
                    style: { fontSize: 16 },
                  },
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '28px' } }}
                value={draft}
              />
            </Fade>
          )}
        </Box>
        {isRecording ? (
          <>
            <IconButton
              aria-label={t('guida.assistant.cancelRecording', {
                defaultValue: 'Annulla la registrazione',
              })}
              disabled={isSending}
              onClick={cancel}
              sx={{ height: 48, width: 48 }}
            >
              <CloseRoundedIcon />
            </IconButton>
            <RecordingLiveDot />
            <IconButton
              aria-label={t('guida.assistant.stop', { defaultValue: 'Ferma e invia il vocale' })}
              aria-pressed="true"
              disabled={isSending}
              onClick={() => swallow(stopRecording())}
              sx={{
                bgcolor: 'error.main',
                color: 'error.contrastText',
                height: 48,
                width: 48,
                '&:hover': { bgcolor: 'error.dark' },
              }}
            >
              <StopRoundedIcon />
            </IconButton>
          </>
        ) : (
          <>
            <IconButton
              aria-label={t('guida.assistant.attach', { defaultValue: 'Allega un documento' })}
              disabled={isSending}
              onClick={() => fileInputRef.current?.click()}
              sx={{ height: 48, width: 48 }}
            >
              <AttachFileRoundedIcon />
            </IconButton>
            {!voiceDisabled ? (
              <IconButton
                aria-label={t('guida.assistant.record', { defaultValue: 'Registra un vocale' })}
                disabled={isSending}
                onClick={() => swallow(start())}
                sx={{ height: 48, width: 48 }}
              >
                <MicRoundedIcon />
              </IconButton>
            ) : null}
            <IconButton
              aria-label={t('guida.assistant.send', { defaultValue: 'Invia messaggio' })}
              color="primary"
              disabled={(!draft.trim() && !attachment) || isSending}
              onClick={submitDraft}
              sx={{ height: 48, width: 48 }}
            >
              <SendRoundedIcon />
            </IconButton>
          </>
        )}
      </Stack>
    </Box>
  );
}
